import React from "react";

/**
 * Inline SVG/glyph icons (FRONTEND_ARCHITECTURE_STANDARDS §2.2).
 * Status is ALWAYS icon + label, never colour alone (handoff TASK-002 note).
 * No icon-font / image-based icon dependencies.
 */

type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 16, ...rest }: IconProps, path: React.ReactNode, viewBox = "0 0 16 16") {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {path}
    </svg>
  );
}

export function IconCheck(props: IconProps) {
  return base(
    props,
    <path
      d="M2.5 8.5l3.5 3.5 7.5-8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  );
}

export function IconWarning(props: IconProps) {
  return base(
    props,
    <>
      <path
        d="M8 1.8L15 14H1L8 1.8z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M8 6v3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="11.6" r="0.9" fill="currentColor" />
    </>,
  );
}

export function IconError(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>,
  );
}

export function IconInfo(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 7.2v3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.9" fill="currentColor" />
    </>,
  );
}

/** Hourglass — queued / waiting for sync. */
export function IconHourglass(props: IconProps) {
  return base(
    props,
    <path
      d="M4 1.8h8M4 14.2h8M5 1.8v2.2l3 3-3 3v4.2M11 1.8v2.2l-3 3 3 3v4.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  );
}

/** Offline (wifi-off) — header indicator and offline banner. */
export function IconOffline(props: IconProps) {
  return base(
    props,
    <>
      <path
        d="M2 5.6C3.4 4.4 5 3.7 6.8 3.6M9.2 3.6c1.8.1 3.4.8 4.8 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M4.4 8.4c.9-.8 1.9-1.3 3-1.4M8.6 7c1.1.1 2.1.6 3 1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11.6" r="1.1" fill="currentColor" />
      <path d="M2.4 2.4l11.2 11.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>,
  );
}

/** Camera glyph for photo capture tiles. */
export function IconCamera(props: IconProps) {
  return base(
    props,
    <>
      <path
        d="M1.8 5.2h2.4l1.2-1.6h5.2l1.2 1.6h2.4v8H1.8v-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8.8" r="2.3" stroke="currentColor" strokeWidth="1.5" />
    </>,
  );
}

/** Chevron used on "Review ›" links. */
export function IconChevronRight(props: IconProps) {
  return base(
    props,
    <path d="M6 3.5l4.5 4.5L6 12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
  );
}

/** Left chevron for back navigation. */
export function IconChevronLeft(props: IconProps) {
  return base(
    props,
    <path d="M10 3.5L5.5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
  );
}

/** Spinner — purely CSS-animated arc, no dependency. */
export function IconSpinner(props: IconProps) {
  return (
    <svg
      className={`pg-spin${props.className ? ` ${props.className}` : ""}`}
      width={props.size ?? 16}
      height={props.size ?? 16}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
